import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, StickyNote } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { CHECKLIST_STATUSES, CHECKLIST_STATUS_IDS, EMPTY_CHECKLIST_ROW } from "@/data/agreements/masterChecklist";
import CategoryIcon, { STATUS_ICON } from "./CategoryIcon";
import { LevelPill } from "./DocumentCard";

const RENEWAL_WINDOW_DAYS = 90;

const STATUS_TRIGGER = {
  "not-started": "text-muted-foreground",
  "in-progress": "border-pink-300 bg-pink-50 text-pink-900",
  signed: "border-emerald-300 bg-emerald-50 text-emerald-900",
  "not-needed": "text-muted-foreground line-through decoration-muted-foreground/50",
};

/** Whole days from today to an ISO date string. null when absent or unparseable. */
export function daysUntil(value, today = new Date()) {
  if (!value || typeof value !== "string") return null;
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value;
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return null;
  const start = new Date(today.getTime());
  start.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - start.getTime()) / 86400000);
}

const fieldLabel = "mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground";

/**
 * ChecklistRow — one document's row in the CPG Agreement Master Checklist.
 *
 * props
 *   doc       the document entry (needs id + name; category is used for the glyph)
 *   row       the stored record; missing keys fall back to EMPTY_CHECKLIST_ROW
 *   level     optional plan level, shown as a pill beside the name
 *   onChange  (patch) => void — receives ONLY the keys that changed
 */
export default function ChecklistRow({ doc, row, level, onChange, className }) {
  const data = { ...EMPTY_CHECKLIST_ROW, ...(row || {}) };
  const [notesOpen, setNotesOpen] = useState(Boolean(data.notes));

  if (!doc || !doc.id) return null;

  const patch = (next) => onChange?.(next);
  const status = CHECKLIST_STATUS_IDS.includes(data.status) ? data.status : "not-started";
  const StatusIcon = STATUS_ICON[status] || STATUS_ICON["not-started"];
  const renewsIn = daysUntil(data.expirationDate);
  const renewing = renewsIn !== null && renewsIn >= 0 && renewsIn <= RENEWAL_WINDOW_DAYS;

  const idFor = (key) => `checklist-${doc.id}-${key}`;

  return (
    <div className={cn("rounded-lg border bg-background p-3 md:p-2.5", className)}>
      <div className="grid gap-3 md:grid-cols-12 md:items-end md:gap-2">
        {/* Document */}
        <div className="min-w-0 md:col-span-3">
          <div className="flex items-start gap-2">
            <CategoryIcon categoryId={doc.category} size="sm" className="mt-0.5" />
            <div className="min-w-0">
              <Link
                to={`/agreements/library/${doc.id}`}
                className="-my-1 block py-1 text-sm font-medium leading-snug hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {doc.name || doc.id}
              </Link>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <LevelPill level={level} className="text-[10px]" />
                {doc.subcategory && <span className="text-[11px] text-muted-foreground">{doc.subcategory}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="min-w-0 md:col-span-2">
          <Label htmlFor={idFor("status")} className={fieldLabel}>
            Status
          </Label>
          <Select value={status} onValueChange={(v) => patch({ status: v })}>
            {/* The trigger's own [&>span]:line-clamp-1 turns this span into a webkit box and stacks
                the glyph above the label; [&>span]:flex displaces it via tailwind-merge. */}
            <SelectTrigger
              id={idFor("status")}
              className={cn(
                "w-full [&>span]:!flex [&>span]:min-w-0 [&>span]:items-center [&>span]:gap-1.5",
                STATUS_TRIGGER[status],
              )}
            >
              <span>
                <StatusIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">
                  <SelectValue placeholder="Not started" />
                </span>
              </span>
            </SelectTrigger>
            <SelectContent>
              {CHECKLIST_STATUSES.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Counterparty */}
        <div className="min-w-0 md:col-span-3">
          <Label htmlFor={idFor("counterparty")} className={fieldLabel}>
            Counterparty
          </Label>
          <Input
            id={idFor("counterparty")}
            value={data.counterparty || ""}
            onChange={(e) => patch({ counterparty: e.target.value })}
            placeholder="Who signs the other side"
          />
        </div>

        {/* Dates — side by side even on a phone. `min-w-0` on children
            allows the date inputs to shrink inside the flex/grid parent
            so the card never overflows on narrow viewports. */}
        <div className="grid grid-cols-2 gap-2 md:col-span-4">
          <div className="min-w-0">
            <Label htmlFor={idFor("effective")} className={fieldLabel}>
              Effective
            </Label>
            <Input
              id={idFor("effective")}
              type="date"
              value={data.effectiveDate || ""}
              onChange={(e) => patch({ effectiveDate: e.target.value })}
              className="w-full px-2"
            />
          </div>
          <div className="min-w-0">
            <Label htmlFor={idFor("expiration")} className={fieldLabel}>
              Expires
            </Label>
            <Input
              id={idFor("expiration")}
              type="date"
              value={data.expirationDate || ""}
              onChange={(e) => patch({ expirationDate: e.target.value })}
              className="w-full px-2"
            />
            {renewing && (
              <Badge variant="outline" className="mt-1 max-w-full gap-1 border-pink-200 bg-pink-50 text-[10px] font-medium text-pink-800 whitespace-normal break-words">
                <CalendarClock className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  {renewsIn === 0 ? "Renews today" : `Renews in ${renewsIn} day${renewsIn === 1 ? "" : "s"}`}
                </span>
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t pt-2.5">
        <label htmlFor={idFor("attorney")} className="flex cursor-pointer items-center gap-2 py-1.5 text-xs text-muted-foreground">
          <input
            id={idFor("attorney")}
            type="checkbox"
            checked={Boolean(data.attorneyReviewed)}
            onChange={(e) => patch({ attorneyReviewed: e.target.checked })}
            className="h-4 w-4 shrink-0 cursor-pointer rounded border-input accent-indigo-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
          Attorney reviewed
        </label>

        <label htmlFor={idFor("stored")} className="flex cursor-pointer items-center gap-2 py-1.5 text-xs text-muted-foreground">
          <input
            id={idFor("stored")}
            type="checkbox"
            checked={Boolean(data.signedCopyStored)}
            onChange={(e) => patch({ signedCopyStored: e.target.checked })}
            className="h-4 w-4 shrink-0 cursor-pointer rounded border-input accent-indigo-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
          Signed copy stored
        </label>

        <button
          type="button"
          onClick={() => setNotesOpen((o) => !o)}
          aria-expanded={notesOpen}
          aria-controls={idFor("notes")}
          className={cn(
            "ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium hover:bg-muted",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            data.notes ? "text-foreground" : "text-muted-foreground",
          )}
        >
          <StickyNote className="h-3.5 w-3.5" />
          Notes
          {data.notes ? <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-indigo-500" aria-hidden="true" /> : null}
        </button>
      </div>

      {notesOpen && (
        <div className="mt-2">
          <Label htmlFor={idFor("notes")} className="sr-only">
            Notes for {doc.name || doc.id}
          </Label>
          <Textarea
            id={idFor("notes")}
            rows={3}
            value={data.notes || ""}
            onChange={(e) => patch({ notes: e.target.value })}
            placeholder="Where the signed copy lives, what is still open, who owes what."
            className="min-h-[72px] text-sm"
          />
        </div>
      )}
    </div>
  );
}
